/**
 * ============================================================
 * Style Playground — Auto-generated metadata
 * ============================================================
 * Device: desktop
 * Language: English
 *
 * ## Style Direction (from analysis)
 * <style-name>ObsidianCyber</style-name>
 * **Mode**: Dark
 * **Style Recipe**: Dark / Cyber Glass + Floating / Borderless + Balanced
 * **Implementation Techniques**: A premium, "developer-mode" aesthetic. Backgrounds are deep, almost absolute black. Cards and surfaces are translucent dark grays (`rgba(255, 255, 255, 0.03)`) with a strong background blur (`backdrop-filter`). Instead of heavy shadows, depth is created using subtle 1px inner borders that catch virtual light (e.g., `box-shadow: inset 0 1px 0 rgba(255,255,255,0.1)`). Component previews sit inside these glass panels. Typography is sleek and futuristic, using a font like Geist or Roboto Mono.
 * **Color Direction**: Deep obsidian/zinc background (`#09090B`). Accent colors are high-saturation, neon-leaning hues that pop aggressively against the dark background—such as Neon Cyan for primary actions and Acid Green or Hot Magenta for status badges (Published, Draft). The overall feel is like a high-end IDE or command center.
 * ============================================================
 */

import React, { useState } from 'react';
import { Icon } from '@iconify/react';

export default function ObsidianCyber() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchFocused, setSearchFocused] = useState(false);

  // Mock Data
  const stats = [
    { label: 'Total Components', value: '142', trend: '+12%', trendUp: true, color: 'text-cyan-400', glow: 'shadow-[0_0_15px_rgba(34,211,238,0.3)]' },
    { label: 'In Draft', value: '12', trend: '-2%', trendUp: false, color: 'text-fuchsia-400', glow: 'shadow-[0_0_15px_rgba(232,121,249,0.3)]' },
    { label: 'In Review', value: '5', trend: '0%', trendUp: true, color: 'text-amber-400', glow: 'shadow-[0_0_15px_rgba(251,191,36,0.3)]' },
    { label: 'Published', value: '125', trend: '+8%', trendUp: true, color: 'text-emerald-400', glow: 'shadow-[0_0_15px_rgba(52,211,153,0.3)]' },
  ];

  const recentUpdates = [
    { id: 1, name: 'Button', version: 'v1.2.0', status: 'Published', time: '2 hours ago', user: 'JD' },
    { id: 2, name: 'Checkbox', version: 'v0.9.0', status: 'Draft', time: '5 hours ago', user: 'AL' },
    { id: 3, name: 'Select', version: 'v2.0.1', status: 'Review', time: '1 day ago', user: 'JD' },
    { id: 4, name: 'Modal', version: 'v1.1.5', status: 'Published', time: '2 days ago', user: 'MK' },
    { id: 5, name: 'Tooltip', version: 'v1.0.0', status: 'Published', time: '3 days ago', user: 'AL' },
  ];

  const activityLog = [
    { id: 1, action: 'JD published v1.2.0 of Button', time: '2h ago', icon: 'lucide:rocket' },
    { id: 2, action: 'AL created draft Checkbox', time: '5h ago', icon: 'lucide:file-edit' },
    { id: 3, action: 'JD requested review for Select', time: '1d ago', icon: 'lucide:eye' },
    { id: 4, action: 'Team updated core design tokens', time: '2d ago', icon: 'lucide:palette' },
  ];

  return (
    <div className="flex h-screen w-full bg-[#050505] text-zinc-300 font-mono selection:bg-cyan-500/30 overflow-hidden">
      
      {/* Background Effects */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-cyan-900/10 blur-[120px] rounded-full mix-blend-screen" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[40%] h-[60%] bg-fuchsia-900/10 blur-[120px] rounded-full mix-blend-screen" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDE1Ii8+Cjwvc3ZnPg==')] opacity-50" />
      </div>

      {/* Sidebar - Floating Panel */}
      <div className="z-10 w-64 p-4 flex flex-col">
        <div className="flex-1 bg-zinc-900/40 backdrop-blur-xl border border-zinc-800/50 rounded-2xl flex flex-col overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
          
          <div className="p-6 flex items-center gap-3 border-b border-zinc-800/50">
            <div className="w-8 h-8 rounded bg-cyan-500/20 border border-cyan-500/50 flex items-center justify-center text-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.2)]">
              <Icon icon="lucide:layers" className="text-xl" />
            </div>
            <span className="font-bold text-zinc-100 tracking-wider text-sm">COMP_HUB</span>
          </div>

          <div className="p-4 flex-1 space-y-2">
            {[
              { id: 'dashboard', icon: 'lucide:layout-dashboard', label: 'Dashboard' },
              { id: 'components', icon: 'lucide:box', label: 'Components' },
              { id: 'tokens', icon: 'lucide:palette', label: 'Design Tokens' },
              { id: 'changelog', icon: 'lucide:git-commit', label: 'Changelog' },
              { id: 'categories', icon: 'lucide:folder-tree', label: 'Categories' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-sm ${
                  activeTab === item.id
                    ? 'bg-zinc-800/80 text-cyan-400 border border-cyan-500/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_0_15px_rgba(34,211,238,0.1)]'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/40 border border-transparent'
                }`}
              >
                <Icon icon={item.icon} className="text-lg" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <div className="p-4 border-t border-zinc-800/50">
            <div className="flex items-center gap-3 px-4 py-2">
              <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs text-zinc-400">
                JD
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-zinc-300">Jane Doe</span>
                <span className="text-[10px] text-zinc-600">Admin</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="z-10 flex-1 flex flex-col h-full py-4 pr-4 pl-0 overflow-hidden">
        
        {/* Header */}
        <header className="h-16 mb-4 flex items-center justify-between px-2">
          <h1 className="text-xl font-bold text-zinc-100 tracking-wide">SYSTEM_DASHBOARD</h1>
          
          <div className={`flex items-center bg-zinc-900/50 backdrop-blur-md border rounded-full px-4 py-2 w-64 transition-all duration-300 ${searchFocused ? 'border-cyan-500/50 shadow-[0_0_15px_rgba(34,211,238,0.15)]' : 'border-zinc-800/80'}`}>
            <Icon icon="lucide:search" className={`text-sm mr-2 ${searchFocused ? 'text-cyan-400' : 'text-zinc-500'}`} />
            <input 
              type="text" 
              placeholder="Search components..." 
              className="bg-transparent border-none outline-none text-sm text-zinc-200 w-full placeholder:text-zinc-600"
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            <div className="text-[10px] text-zinc-600 border border-zinc-800 px-1.5 py-0.5 rounded bg-zinc-900">⌘K</div>
          </div>
        </header>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto pr-2 space-y-6 custom-scrollbar">
          
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4">
            {stats.map((stat, i) => (
              <div key={i} className="bg-zinc-900/40 backdrop-blur-md border border-zinc-800/60 rounded-2xl p-5 flex flex-col relative overflow-hidden group shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                <div className={`absolute top-0 left-0 w-full h-[1px] ${stat.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <span className="text-xs text-zinc-500 uppercase tracking-wider mb-2">{stat.label}</span>
                <div className="flex items-end justify-between mt-auto">
                  <span className={`text-3xl font-bold ${stat.color}`}>{stat.value}</span>
                  <div className={`flex items-center text-xs ${stat.trendUp ? 'text-emerald-400/80' : 'text-fuchsia-400/80'}`}>
                    <Icon icon={stat.trendUp ? 'lucide:trending-up' : 'lucide:trending-down'} className="mr-1" />
                    {stat.trend}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-6">
            
            {/* Recent Updates Area */}
            <div className="col-span-2 space-y-4">
              <h2 className="text-sm font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                <Icon icon="lucide:activity" className="text-cyan-500" /> Recent Updates
              </h2>
              
              <div className="bg-zinc-900/40 backdrop-blur-md border border-zinc-800/60 rounded-2xl overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                <div className="grid grid-cols-12 gap-4 p-4 border-b border-zinc-800/40 text-xs text-zinc-500 uppercase tracking-wider bg-zinc-900/20">
                  <div className="col-span-4">Component</div>
                  <div className="col-span-2">Version</div>
                  <div className="col-span-3">Status</div>
                  <div className="col-span-3 text-right">Updated</div>
                </div>
                
                <div className="divide-y divide-zinc-800/40">
                  {recentUpdates.map((item) => (
                    <div key={item.id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-zinc-800/30 transition-colors cursor-pointer group">
                      <div className="col-span-4 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-zinc-800 border border-zinc-700/50 flex items-center justify-center text-zinc-400 group-hover:border-cyan-500/30 group-hover:text-cyan-400 transition-colors">
                          <Icon icon="lucide:box" />
                        </div>
                        <span className="font-medium text-zinc-200">{item.name}</span>
                      </div>
                      <div className="col-span-2 text-xs font-mono text-zinc-400">{item.version}</div>
                      <div className="col-span-3 flex items-center">
                        <span className={`px-2 py-1 rounded-md text-[10px] uppercase tracking-wider border flex items-center gap-1.5
                          ${item.status === 'Published' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 
                            item.status === 'Draft' ? 'bg-fuchsia-500/10 border-fuchsia-500/20 text-fuchsia-400' : 
                            'bg-amber-500/10 border-amber-500/20 text-amber-400'}`}
                        >
                          <div className={`w-1.5 h-1.5 rounded-full ${item.status === 'Published' ? 'bg-emerald-400' : item.status === 'Draft' ? 'bg-fuchsia-400' : 'bg-amber-400'}`} />
                          {item.status}
                        </span>
                      </div>
                      <div className="col-span-3 text-right text-xs text-zinc-500 flex flex-col items-end">
                        <span>{item.time}</span>
                        <span className="text-[10px] text-zinc-600">by {item.user}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions & Activity Area */}
            <div className="col-span-1 space-y-6">
              
              {/* Actions */}
              <div className="space-y-3">
                <button className="w-full relative group overflow-hidden bg-cyan-950/30 border border-cyan-500/30 hover:border-cyan-400/60 rounded-xl p-4 flex items-center justify-center gap-2 text-cyan-400 transition-all duration-300 shadow-[0_0_20px_rgba(34,211,238,0.05)] hover:shadow-[0_0_20px_rgba(34,211,238,0.15)]">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/10 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <Icon icon="lucide:plus" className="text-lg" />
                  <span className="font-bold tracking-wide text-sm">NEW COMPONENT</span>
                </button>
                <button className="w-full bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 rounded-xl p-3 flex items-center justify-center gap-2 text-zinc-400 hover:text-zinc-200 transition-colors text-sm">
                  <Icon icon="lucide:settings-2" />
                  <span>Manage Tokens</span>
                </button>
              </div>

              {/* Activity Feed */}
              <div className="bg-zinc-900/40 backdrop-blur-md border border-zinc-800/60 rounded-2xl p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">System Log</h3>
                
                <div className="space-y-4 relative before:absolute before:inset-y-0 before:left-[11px] before:w-[1px] before:bg-zinc-800/60">
                  {activityLog.map((log) => (
                    <div key={log.id} className="relative flex gap-4 pl-1">
                      <div className="w-6 h-6 rounded-full bg-zinc-900 border border-zinc-700 z-10 flex items-center justify-center text-zinc-400 shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                        <Icon icon={log.icon} className="text-[10px]" />
                      </div>
                      <div className="flex flex-col pt-0.5">
                        <p className="text-xs text-zinc-300">{log.action}</p>
                        <span className="text-[10px] text-zinc-600 font-mono mt-0.5">{log.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(63, 63, 70, 0.4);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(63, 63, 70, 0.8);
        }
      `}} />
    </div>
  );
}
